import React from 'react';
import IconButton from '@frontend/Components/Inputs/IconButton';
import type { ModalTriggerElement as ModalTriggerElementType } from '@frontend/Models/TopBar';

/**
 * ModalTriggerElement - Button that opens a modal
 * 
 * Triggers a modal to open by calling the openModal callback with the modalId.
 * This component integrates with the modal management system (Task 9).
 * 
 * @param type - Element type identifier ('modalTrigger')
 * @param icon - FontAwesome icon for the button
 * @param ariaLabel - Accessibility label for the button
 * @param modalId - ID of the modal to open
 * @param disabled - Whether the button is disabled
 * @param condition - Optional conditional rendering function
 * @param openModal - Callback to open modal (provided by modal manager)
 */
const ModalTriggerElement: React.FC<ModalTriggerElementType & { openModal?: (modalId: string) => void }> = ({
  icon,
  ariaLabel,
  modalId,
  disabled,
  condition,
  openModal,
}) => {
  // Conditional rendering - don't render if condition returns false
  if (condition && !condition()) {
    return null;
  }

  // Handle button click to open modal
  const handleClick = () => {
    if (openModal) {
      openModal(modalId);
    }
  };

  return (
    <IconButton
      icon={icon}
      ariaLabel={ariaLabel}
      onClick={handleClick}
      disabled={disabled}
      aria-haspopup="dialog"
      className="topBar-modal-trigger"
    />
  );
};

export default ModalTriggerElement;

